import {
    ClientKpiConfiguration,
    KpiDefinition,
    KpiFilters,
    KpiResult,
} from './types';
import ApiService from 'services/apiService';

const definitions: KpiDefinition[] = [
    { id: 'totalParticipantsServed', key: 'totalParticipantsServed', label: 'Total Participants Served', format: 'number', defaultOrder: 1, createdDate: '2026-08-27T00:00:00Z' },
    { id: 'totalServicesDelivered', key: 'totalServicesDelivered', label: 'Total Services Delivered', format: 'number', defaultOrder: 2, createdDate: '2026-08-27T00:00:00Z' },
    { id: 'totalHoursDelivered', key: 'totalHoursDelivered', label: 'Total Hours Delivered', format: 'decimal', defaultOrder: 3, createdDate: '2026-08-27T00:00:00Z' },
    { id: 'averageHoursPerParticipant', key: 'averageHoursPerParticipant', label: 'Average Hours per Participant', format: 'decimal', defaultOrder: 4, createdDate: '2026-08-27T00:00:00Z' },
    { id: 'participantsWithoutServices', key: 'participantsWithoutServices', label: 'Participants Without Services', format: 'number', defaultOrder: 6, supportsParent: false, createdDate: '2026-08-27T00:00:00Z' },
];

const configurationByTab = new Map<string, ClientKpiConfiguration[]>();
const KPI_CONFIGURATION_STORAGE_PREFIX = 'compass.dashboard.kpis.v2';
const LEGACY_KPI_CONFIGURATION_STORAGE_PREFIX = 'compass.dashboard.kpis';

const getConfigurationKey = (dashboardTabId: string, tenantName?: string, userId?: number, prefix = KPI_CONFIGURATION_STORAGE_PREFIX): string =>
    `${prefix}:${tenantName ?? 'unknown'}:${userId ?? 'unknown'}:${dashboardTabId}`;

const getDefaultConfiguration = (dashboardTabId: string): ClientKpiConfiguration[] =>
    definitions.flatMap((definition) => (['student', 'parent'] as const).map((viewType, audienceIndex) => ({
        dashboardTabId,
        kpiId: definition.id,
        viewType,
        enabled: false,
        displayOrder: (definition.defaultOrder * 2) - 1 + audienceIndex,
    })));

export class KpiService {
    static async getAvailableKpis(): Promise<KpiDefinition[]> {
        return definitions.map((definition) => ({ ...definition }));
    }

    static async getClientKpiConfiguration(dashboardTabId: string, tenantName?: string, userId?: number): Promise<ClientKpiConfiguration[]> {
        const configurationKey = getConfigurationKey(dashboardTabId, tenantName, userId);
        let configuration = configurationByTab.get(configurationKey);

        if (!configuration) {
            const storedConfiguration = window.localStorage.getItem(configurationKey);
            if (storedConfiguration) {
                try {
                    const parsedConfiguration = JSON.parse(storedConfiguration);
                    if (Array.isArray(parsedConfiguration)) configuration = parsedConfiguration;
                } catch (error) {
                    console.error('Failed to load saved KPI configuration:', error);
                }
            }
        }

        if (!configuration) {
            const legacyConfigurationKey = getConfigurationKey(
                dashboardTabId,
                tenantName,
                userId,
                LEGACY_KPI_CONFIGURATION_STORAGE_PREFIX
            );
            const legacyConfiguration = window.localStorage.getItem(legacyConfigurationKey);
            if (legacyConfiguration) {
                try {
                    const parsedConfiguration = JSON.parse(legacyConfiguration);
                    if (Array.isArray(parsedConfiguration)) {
                        configuration = parsedConfiguration.map((item: ClientKpiConfiguration) => ({
                            ...item,
                            enabled: item.viewType === 'parent' && item.enabled,
                        }));
                        window.localStorage.setItem(configurationKey, JSON.stringify(configuration));
                    }
                } catch (error) {
                    console.error('Failed to migrate saved KPI configuration:', error);
                }
            }
        }

        configuration ??= getDefaultConfiguration(dashboardTabId);
        configurationByTab.set(configurationKey, configuration);
        return configuration.map((item) => ({ ...item }));
    }

    static async getKpiResults(filters: KpiFilters): Promise<KpiResult[]> {
        const response = await ApiService.instance.get<{ results: KpiResult[] }>(
            '/KpiDashboard/GetKpiResults',
            { params: { schoolYear: filters.schoolYearId, programId: filters.programId } },
        );

        if (response.status !== 200 || !Array.isArray(response.data?.results)) {
            throw new Error('Failed to load KPI results.');
        }

        return response.data.results;
    }

    static async saveClientKpiConfiguration(configuration: ClientKpiConfiguration[], tenantName?: string, userId?: number): Promise<void> {
        const dashboardTabId = configuration[0]?.dashboardTabId;
        if (!dashboardTabId) return;

        const configurationKey = getConfigurationKey(dashboardTabId, tenantName, userId);
        const savedConfiguration = configuration.map((item) => ({ ...item }));
        configurationByTab.set(configurationKey, savedConfiguration);
        window.localStorage.setItem(configurationKey, JSON.stringify(savedConfiguration));
    }
}
